/**
 * Notes API Client
 *
 * All calls include Authorization: Bearer <token> from authStore.
 * No note content in logs. Zero console.log. Zero any types.
 */

import type { Note, NoteBlock } from '@signa/shared';
import { NOTE_API } from './api-config';
import { useAuthStore } from '../stores/authStore';

// Auth header helper

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// API fetch helper

async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = body as { message?: string };
    throw new Error(err.message ?? `HTTP ${res.status}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// Response types (API surface)

export interface NoteListResponse {
  notes: NoteSummary[];
  nextCursor?: string;
}

export interface NoteSummary {
  id: string;
  title: string;
  snippet: string;
  updatedAt: string;
  labels: string[];
  isPinned: boolean;
  isDaily: boolean;
}

// Mappers: API to shared types

/** Derive a short text snippet from blocks for NoteSummary */
function blocksToSnippet(blocks: NoteBlock[]): string {
  const textBlock = blocks.find(
    (b) => b.type === 'paragraph' && b.content.trim().length > 0
  );
  const raw = textBlock?.content ?? '';
  return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
}

function apiNoteToNote(note: Note): Note {
  return note;
}

// Notes API

export const notesApi = {
  list(params?: { limit?: number; cursor?: string }): Promise<NoteListResponse> {
    const url = new URL(NOTE_API.list());
    if (params?.limit) url.searchParams.set('limit', String(params.limit));
    if (params?.cursor) url.searchParams.set('cursor', params.cursor);
    return apiFetch<NoteListResponse>(url.toString());
  },

  get(id: string): Promise<{ note: Note }> {
    return apiFetch<{ note: Note }>(NOTE_API.get(id));
  },

  create(data: { title?: string; blocks: NoteBlock[] }): Promise<{ note: Note }> {
    return apiFetch<{ note: Note }>(NOTE_API.create(), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(
    id: string,
    data: { title?: string; blocks?: NoteBlock[]; isPinned?: boolean; tags?: string[] },
  ): Promise<{ note: Note }> {
    return apiFetch<{ note: Note }>(NOTE_API.update(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(id: string): Promise<void> {
    return apiFetch<void>(NOTE_API.delete(id), { method: 'DELETE' });
  },

  getBacklinks(
    id: string,
  ): Promise<{ backlinks: Array<{ noteId: string; noteTitle: string }> }> {
    return apiFetch<{ backlinks: Array<{ noteId: string; noteTitle: string }> }>(
      NOTE_API.backlinks(id),
    );
  },

  getDailyNote(date: string): Promise<{ note: Note | null }> {
    return apiFetch<{ note: Note | null }>(NOTE_API.daily(date));
  },
};
